import { DateUtil } from "../model/DateUtil.js";
import { Translation } from "../model/Language.js";
import { BaseElementOptions, BaseElement } from "./BaseElement.js";
import { Menu, MenuButton, MenuOptions } from "./Menu.js";

export interface GalleryOptions extends BaseElementOptions {
    uid?: string;
}

export interface GalleryOpenOptions {
    id?: string;
    index?: number;
    initiator?: HTMLElement;
    fromHistory?: boolean;
    items: GalleryItemData[];
}

export interface GalleryItemData {
    id: string;
    type: "video" | "image";
    url: string;
    width: number;
    height: number;
    title: Translation;
    author: Translation;
    date: Date;
    poster: string;
}

export class Gallery extends BaseElement {

    private _view: e5.ScrollView;
    private _header: HTMLDivElement;
    private _headerMenu: Menu;
    private _body: HTMLDivElement;
    private _footer: HTMLDivElement;
    private _footerMenu: Menu;
    private _counter: MenuButton;
    private _prev: MenuButton;
    private _next: MenuButton;
    private _close: MenuButton;
    private _title: MenuButton;
    private _author: MenuButton;
    private _date: MenuButton;
    private _initiator: HTMLElement = null;
    private _image: HTMLImageElement;
    private _video: HTMLVideoElement;
    private _current: GalleryItemData;
    private _items: GalleryItemData[] = [];
    private _videoControl: VideoControl;

    private static _uid: number = 0;
    private _session: number = Math.random();
    private _uid: string;

    public create(options: GalleryOptions): this {
        options = e5.object.merge({
            orientation: "vertical",
            uid: "gallery"
        } as GalleryOptions, options);

        if (!options.uid) {
            this._uid = "gallery-" + (++Gallery._uid);
        } else {
            this._uid = options.uid;
        }

        super.create(options);

        var e = document.createElement("div");
        e.className = "awi-gallery e5-hidden";
        e.innerHTML = `
        <div class="awi-gallery__header"></div>
        <div class="awi-gallery__body">
            <div class="awi-gallery__body-background"></div>
        </div>
        <div class="awi-gallery__footer"></div>
        `;

        this._header = e.querySelector(".awi-gallery__header");
        this._body = e.querySelector(".awi-gallery__body");
        this._footer = e.querySelector(".awi-gallery__footer");

        this._view = new e5.ScrollView();
        // this._view.resizeBehaviour = e5.ScrollViewResize.ContainMaxOriginal;
        this._view.resizeBehaviour = e5.ScrollViewResize.Center;
        this._view.elasticityX = 0;
        this._view.elasticityY = 0;
        this._body.appendChild(this._view.element);

        this._headerMenu = new Menu().create({
            container: this._header
        });

        this._footerMenu = new Menu().create({
            container: this._footer
        });

        this._image = document.createElement("img");
        this._video = document.createElement("video");
        this._video.setAttribute("playsinline", "");
        this._video.setAttribute("disablepictureinpicture", "");
        this._video.setAttribute("controlslist", "nodownload");
        this._video.disablePictureInPicture = true;
        this._video.disableRemotePlayback = true;

        this._videoControl = new VideoControl().create({
            video: this._video,
            container: this._body,
            // orientation: "horizontal"
        });

        this.setElement(e);

        var swipe = new e5.Swipe(this._view.element);
        swipe.onSwipe.add((direction) => {
            if (this._view.hasOverflowHorizontal()) {
                return;
            }
            if (direction === "left") {
                this.next();
            }
            else if (direction === "right") {
                this.prev();
            }
        });

        this._title = this._headerMenu.addButton({
            label: "--",
            position: "start",
            tagName: "div"
        });

        this._date = this._headerMenu.addButton({
            label: "--",
            position: "start",
            tagName: "div"
        });

        this._counter = this._footerMenu.addButton({
            label: "--",
            position: "end",
            tagName: "div"
        });

        this._prev = this._footerMenu.addButton({
            icon: "fal fa-chevron-left",
            position: "end",
            ariaLabel: {
                de: "Vorheriges Bild anzeigen",
                en: "Goto Previous image"
            }
        });
        this._prev.element.addEventListener("click", () => this.prev());

        this._next = this._footerMenu.addButton({
            icon: "fal fa-chevron-right",
            position: "end",
            ariaLabel: {
                de: "Nächstes Bild anzeigen",
                en: "Goto next image"
            }
        });
        this._next.element.addEventListener("click", () => this.next());

        this._close = this._headerMenu.addButton({
            icon: "fal fa-times",
            position: "end",
            ariaLabel: {
                de: "Gallerie schließen",
                en: "Close galery"
            }
        });
        this._close.element.addEventListener("click", () => this.close());

        this._author = this._footerMenu.addButton({
            label: "--",
            position: "start",
            tagName: "div"
        });



        window.addEventListener("popstate", (e) => {

            if (e.state
                && e.state.action === this._uid
                && e.state.session === this._session) {
                if (this._openOptions) {
                    this._openOptions.fromHistory = true;
                    this.open(this._openOptions);
                }
                // window.history.back();
            } else if (this.isOpen) {
                this.close({
                    fromHistory: true
                });
            }
        });

        return this;
    }

    private next(): void {
        if (!this._current) {
            return;
        }
        var index = this._items.indexOf(this._current);
        if (index >= this._items.length - 1) {
            return;
        }
        index = e5.Calc.clamp(index + 1, 0, this._items.length - 1);
        this.showItem(this._items[index], false);
    }

    private prev(): void {
        if (!this._current) {
            return;
        }
        var index = this._items.indexOf(this._current);
        if (index <= 0) {
            return;
        }
        index = e5.Calc.clamp(index - 1, 0, this._items.length - 1);
        this.showItem(this._items[index], false);
    }

    private _openOptions: GalleryOpenOptions = null;

    public open(options: GalleryOpenOptions): void {

        this._items = options.items;
        this._openOptions = e5.ObjectUtil.clone(options);

        document.documentElement.classList.add("awi-gallery--open");

        if (this._initiator) {
            this._initiator.setAttribute("aria-expanded", "false");
        }
        this._initiator = options.initiator;
        if (this._initiator) {
            this._initiator.setAttribute("aria-expanded", "true");
        }
        var wasHidden = this.element.classList.contains("e5-hidden");

        e5.tabTrap.push({ element: this.element });

        this.element.classList.remove("e5-hidden");

        if (wasHidden) {
            e5.tabTrap.focus(this._close.element);
        }

        if (options.id) {
            this.showItem(this.getData(options.id), true);
        } else if (options.index) {
            this.showItem(this._items[options.index], true);
        } else {
            console.warn("Could not open gallery");
        }

        if (!options.fromHistory) {
            window.history.pushState({
                action: this._uid,
                session: this._session
            }, "", "#" + this._uid);
        }
    }

    private get isOpen(): boolean {
        return !this.element.classList.contains("awi-gallery--hidden");
    }

    private close(options: { fromHistory?: boolean } = {}): void {
        e5.tabTrap.pop();
        
        document.documentElement.classList.remove("awi-gallery--open");
        this.element.classList.add("e5-hidden");

        if (!options.fromHistory) {
            window.history.back();
        }

        if (this._initiator) {
            var ini = this._initiator;
            setTimeout(() => {
                if (ini !== this._initiator)
                    return;
                ini.setAttribute("aria-expanded", "false");
                ini.focus();
                this._initiator = null;
            });
        }
    }

    private getData(id: string): GalleryItemData {
        for (var d of this._items) {
            if (d.id == id) {
                return d;
            }
        }
        return null;
    }

    private showItem(item: GalleryItemData, autoplay: boolean): void {

        this._video.removeAttribute("src");
        this._image.removeAttribute("src");

        if (!item) {
            return;
        }

        this._openOptions.id = item.id;
        this._current = item;

        // if(!item){
        //     this._prev.visible = false;
        //     this._next.visible = false;
        //     this._title.visible = false;
        //     this._date.visible = false;
        //     this._author.visible = false;
        //     return;
        // } else {

        // }

        var index = this._items.indexOf(item);
        this._counter.label = (index + 1) + " / " + this._items.length;
        this._prev.disabled = index <= 0;
        this._next.disabled = index >= this._items.length - 1;
        this._title.label = item.title;
        this._date.label = {
            de: DateUtil.toStringReadableShort(item.date, "de"),
            en: DateUtil.toStringReadableShort(item.date, "en")
        }
        this._author.label = lang.type("html", item.author);

        if (item.type === "image") {
            var img = this._image;
            img.style.display = "none";
            img.width = item.width;
            img.height = item.height;
            img.src = item.url;
            img.style.display = "block";
            this._videoControl.visible = false;
            this._view.content = {
                element: img,
                width: item.width,
                height: item.height
            };
        }
        else if (item.type === "video") {
            var vid = this._video;
            vid.style.display = "none";
            vid.width = item.width;
            vid.height = item.height;
            vid.src = item.url;
            vid.poster = item.poster || "";
            vid.style.display = "block";
            this._view.content = {
                element: vid,
                width: item.width,
                height: item.height
            };
            this._videoControl.visible = true;

            if (autoplay) {
                try {
                    vid.play();
                } catch (error) {

                }
            }
        }
        this._view.applyScale(e5.ScrollViewScale.ContainMaxOriginal);
    }
}

interface VideoControlOptions extends MenuOptions {
    video: HTMLVideoElement;
}

class VideoControl extends Menu {
    public create(options: VideoControlOptions): this {

        super.create(options);

        this.element.classList.add("awi-video-control");

        var video = options.video;



        var play = this.addButton({
            icon: "fal fa-play",
            iconActive: "fal fa-pause",
            ariaLabel: {
                de: "Abspielen",
                en: "Play"
            }
        });
        play.element.addEventListener("click", () => {
            if (video.paused) {
                video.play();
            } else {
                video.pause();
            }
        });

        var updatePlay = () => {
            if (video.paused || video.ended) {
                play.active = false;
            }
            else {
                play.active = true;
            }
        };
        video.addEventListener("playing", updatePlay);
        video.addEventListener("pause", updatePlay);
        video.addEventListener("ended", updatePlay);
        video.addEventListener("abort", updatePlay);
        video.addEventListener("error", updatePlay);

        var mute = this.addButton({
            icon: "fal fa-volume-mute",
            iconActive: "fal fa-volume",
            ariaLabel: {
                de: "Ton an",
                en: "Volume on"
            },
            ariaLabelActive: {
                de: "Ton aus",
                en: "Mute"
            }
        });
        var updateMute = () => {
            mute.active = !video.muted && video.volume >= 0;
        };
        mute.element.addEventListener("click", () => {
            video.muted = !video.muted;
            updateMute();
        });
        video.addEventListener("volumechange", updateMute);
        updateMute();

        var time = this.addButton({
            label: "--:--",
            ariaLabel: {
                de: "Zeit",
                en: "Time"
            },
            tagName: "div",
            cssClass: "awi-gallery__video-time"
        });
        var updateTime = () => {


            if (!video.duration) {
                time.label = "-- : --";
            } else {
                time.label = this.formatTime(video.currentTime) + " / " + this.formatTime(video.duration);
            }
        };
        video.addEventListener("timeupdate", updateTime);
        video.addEventListener("durationchange", updateTime);

        var re = video.requestFullscreen
            || (video as any).webkitRequestFullscreen
            || (video as any).webkitEnterFullscreen;
        if (re !== undefined) {
            var f = this.addButton({
                icon: "fal fa-expand"
            });

            f.element.addEventListener("click", () => {
                if (video.requestFullscreen !== undefined) {
                    video.requestFullscreen();
                }
                else if ((video as any).webkitRequestFullscreen !== undefined) {
                    (video as any).webkitRequestFullscreen();
                }
                else if ((video as any).webkitEnterFullscreen !== undefined) {
                    (video as any).webkitEnterFullscreen();
                }
            });
        }

        return this;
    }

    private formatTime(sec: number): string {
        var hours = Math.floor(sec / 3600);
        (hours >= 1) ? sec = sec - (hours * 3600) : hours = 0;
        var min = Math.floor(sec / 60);
        (min >= 1) ? sec = sec - (min * 60) : min = 0;
        (sec < 1) ? sec = 0 : 0;


        if (hours > 0) {
            return hours
                + ':'
                + e5.Text.fillHead(min + "", 2, "0")
                + ':'
                + e5.Text.fillHead(Math.floor(sec) + "", 2, "0");
        } else {
            return min + ""
                + ':'
                + e5.Text.fillHead(Math.floor(sec) + "", 2, "0");
        }
    }
}